/**
 * What the URL was asking for when the app started.
 *
 * <strong>This is new plumbing, not a new component, and it is the step of the email
 * work most likely to be under-estimated.</strong> The portal has no router, no
 * `window.location` usage and no `URLSearchParams` outside one request builder — but
 * every token-bearing link this platform now sends arrives as a query string on a cold
 * load: an unsubscribe that has already happened, and the password reset that has never
 * worked because nothing was ever reading the token out of the address bar.
 *
 * Two properties matter and both come from reading once, here, at module load:
 *
 * - It is read <strong>before</strong> `currentPage` initialises, so the first render is
 *   already on the right screen. Reading it in an effect would flash the home page first
 *   and then replace it, which on a reset link looks like the link did not work.
 * - It is a plain snapshot rather than live state, so nothing re-reads a URL that
 *   {@link clearQuery} has since tidied up.
 *
 * The screens these choose are rendered <strong>outside</strong> the `isLoggedIn` guard,
 * which is the other half of the same point: somebody following a reset link has by
 * definition failed to log in, and somebody clicking unsubscribe from a mail client has
 * no session at all.
 */

function read() {
  if (typeof window === 'undefined') {
    return { page: null, resetToken: null, unsubscribed: false };
  }
  const params = new URLSearchParams(window.location.search);
  const path = window.location.pathname || '';

  return {
    // Which section an email link wants. Only ever one of the app's own page ids, and
    // an unknown value falls through to the default rather than rendering nothing.
    page: params.get('page'),

    // The reset link's token. The path is what distinguishes it from any other use of
    // ?token=, and it matches app.password-reset.url-path on the server - the two have
    // to agree or the link opens the home page with a credential in the address bar.
    resetToken: path.startsWith('/reset-password') ? params.get('token') : null,

    // The unsubscribe already happened server-side before the redirect that landed
    // here; this is only the confirmation.
    unsubscribed: params.get('unsubscribed') === '1',
  };
}

export const launchParams = read();

/**
 * Take the parameters back out of the address bar without reloading.
 *
 * <strong>Called as soon as a token has been used</strong>, because a reset token is a
 * single-use bearer credential for that account and leaving it in the URL leaves it in
 * browser history, in the next screenshot, and in whatever the person pastes when they
 * ask why it did not work.
 *
 * A no-op where `history` is unavailable, which keeps the test environment out of it.
 */
export function clearQuery() {
  if (typeof window === 'undefined' || !window.history || !window.history.replaceState) {
    return;
  }
  window.history.replaceState({}, '', window.location.pathname);
}
